import { createContext, useContext } from 'react'
import {
  DEFAULT_MAP_STYLE_MODE,
  type MapStyleMode,
} from 'tripPlanner/components/ChatMap/map3DConfig'

export interface MapInteractionContextValue {
  /** Called when a hotel card is hovered (from carousel or explore drawer). Updates isCardHoveredRef + hoveredHotelId. */
  onCardHover: (offerId: string | undefined) => void
  /** Called when a map marker is hovered. Guards against clearing hover while a card is actively hovered. */
  onMarkerHover: (markerId: string | undefined) => void
  /** Currently highlighted hotel/marker ID for visual sync between map and card lists */
  highlightedMarkerId: string | undefined
  /** Shared main-map style mode so drawer maps can stay in sync */
  mapStyleMode: MapStyleMode
  onMapStyleModeChange: (mode: MapStyleMode) => void
}

const MapInteractionContext = createContext<MapInteractionContextValue | undefined>(undefined)

export const MapInteractionProvider = MapInteractionContext.Provider

export function useMapInteraction(): MapInteractionContextValue {
  const ctx = useContext(MapInteractionContext)
  if (!ctx) throw new Error('useMapInteraction must be used within MapInteractionProvider')
  return ctx
}

/** Soft read for drawer maps that may render outside the provider in tests. */
export function useMapInteractionStyle(): {
  mapStyleMode: MapStyleMode
  onMapStyleModeChange: ((mode: MapStyleMode) => void) | undefined
} {
  const ctx = useContext(MapInteractionContext)
  return {
    mapStyleMode: ctx?.mapStyleMode ?? DEFAULT_MAP_STYLE_MODE,
    onMapStyleModeChange: ctx?.onMapStyleModeChange,
  }
}
